import { type SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePostMessage } from '@/entities/feedback';
import { useErrorMessage } from '@/shared/i18n';
import { Button, TextAreaField } from '@/shared/ui';

import { AttachmentPicker } from './AttachmentPicker';
import { useAttachmentDraft } from './use-attachment-draft';
import styles from './ReplyComposer.module.css';

interface Props {
  suggestionId: string;
  /** Moderators can switch a message into a private internal note. */
  staff: boolean;
}

/**
 * Reply box. Sending only adds a message: it never changes the status. A pasted screenshot
 * becomes an attachment here too.
 */
export function ReplyComposer({ suggestionId, staff }: Props) {
  const { t } = useTranslation();
  const getMessage = useErrorMessage();
  const post = usePostMessage(suggestionId);
  const draft = useAttachmentDraft();
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const busy = post.isPending || draft.uploading;
  const sending = useRef(false);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (text.trim() === '' || busy || sending.current) return;
    sending.current = true;
    post.mutate(
      { content: text.trim(), internal, attachment_ids: draft.ids },
      {
        onSuccess: () => {
          setText('');
          draft.clear();
        },
        onSettled: () => {
          sending.current = false;
        },
      },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit} onPaste={draft.onPaste}>
      {staff && (
        <fieldset className={styles.kind}>
          <legend className={styles.legend}>{t('feedback.reply.kind')}</legend>
          <label className={styles.radio}>
            <input
              type="radio"
              name={`kind-${suggestionId}`}
              checked={!internal}
              onChange={() => {
                setInternal(false);
              }}
            />
            {t('feedback.reply.public')}
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name={`kind-${suggestionId}`}
              checked={internal}
              onChange={() => {
                setInternal(true);
              }}
            />
            {t('feedback.reply.internal')}
          </label>
        </fieldset>
      )}
      <TextAreaField
        label={internal ? t('feedback.reply.internalLabel') : t('feedback.reply.label')}
        value={text}
        rows={4}
        maxLength={5000}
        placeholder={
          internal ? t('feedback.reply.internalPlaceholder') : t('feedback.reply.placeholder')
        }
        hint={internal ? t('feedback.reply.internalHint') : undefined}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <AttachmentPicker draft={draft} compact />
      {post.isError && (
        <p className={styles.error} role="alert">
          {getMessage(post.error)}
        </p>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={busy || text.trim() === ''}>
          {post.isPending
            ? t('feedback.form.sending')
            : internal
              ? t('feedback.reply.sendInternal')
              : t('feedback.reply.send')}
        </Button>
      </div>
    </form>
  );
}
